import { useSelector, useDispatch } from "react-redux";
import EmptyPage from "../components/SelectClaimType";
import {
  setBatchIdsExpanded,
  setReturnProductsArray,
  setClaimSummaryRows,
  setReturnProductObject,
  setReturnProductObjectInitial,
  setSelectedReturnProductArrayIdx,
  setEditedBatches,
  setSelectedClaimSubType,
  setNonEligible
} from "../../../stateManagement/actions/claims";
import MiniClaimSummary from "../components/MiniClaimSummary";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import { getPurchaseReturnsColumnsMetaData } from "../utils/CreateClaimUtils";
import emptyPageImageSrc from "../../../assets/image/png/emptyPage.png";
import "./PurchaseReturns.css";
import { useEffect, useRef } from "react";
import { modifyReturnProductsArray, returnProductObj } from "../utils/utils";
import { useNavigate } from "react-router-dom";
import { logAnalyticsEvent } from "../../../../src/firebase";

const PurchaseReturns = () => {
  const { returnProductsArray, month, editedBatches, nonEligible, returnProductObjectOriginal } = useSelector(
    (state: any) => state.claims
  );
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {caseUnitLabel, otherUnitLabel, packUnitLabel, batchIdConfiguration, enableFocPurchaseReturnClaims } = ConfigDictionary;
  const purchaseReturnsColumnsMetaData = getPurchaseReturnsColumnsMetaData(caseUnitLabel, otherUnitLabel, packUnitLabel, nonEligible.length > 0, batchIdConfiguration, enableFocPurchaseReturnClaims);
  const returnProductsRef = useRef(returnProductsArray);
  useEffect(() => {
    returnProductsRef.current = returnProductsArray;
  }, [returnProductsArray]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      setClaimSummaryRows(modifyReturnProductsArray(returnProductsArray, editedBatches))
    );
  }, [dispatch, returnProductsArray]);

  const handleAddReturnProduct = () => {
    dispatch(
      setSelectedReturnProductArrayIdx(
        returnProductsArray?.length ? returnProductsArray?.length : 0
      )
    );
    dispatch(setReturnProductObject(returnProductObj));
    navigate("addProduct", { state: { month } });
  };

  const handleClickEditPurchaseReturnsRow = (returnProductObject: any) => {
    const originalDataEntry = returnProductObjectOriginal.find(
      (item: any) => item.skuCode === returnProductObject.skuCode
    );
  
    let productToEdit = returnProductObject;
  
    if (originalDataEntry) {
      let relevantBatches = originalDataEntry.originalData.batchIds;
      
      if (returnProductObject.stockType === "normal") {
        relevantBatches = originalDataEntry.originalData.batchIds.filter(
          (b: any) => b.stockType !== "foc"
        );
      } else if (returnProductObject.stockType === "foc") {
        relevantBatches = originalDataEntry.originalData.batchIds.filter(
          (b: any) => b.stockType === "foc"
        );
      }

      const mergedBatches = relevantBatches.map((originalBatch: any) => {
        const editedBatch = returnProductObject.batchIds?.find(
          (b: any) => b.id === originalBatch.id
        );
        return editedBatch || originalBatch;
      });
  
      productToEdit = {
        ...returnProductObject,
        batchIds: mergedBatches,
        stockType: returnProductObject.stockType
      };
    }
  
    dispatch(setSelectedReturnProductArrayIdx(returnProductObject.id - 1));
    dispatch(setReturnProductObject(productToEdit));
    dispatch(setBatchIdsExpanded(true));
    dispatch(setReturnProductObjectInitial(productToEdit));
    navigate("addProduct");
    logAnalyticsEvent("claim_edit_initiated", {
      distributor_id: JSON.parse(localStorage.authContext).user.loginId,
      claim_type: "Damage Returns",
      claim_subtype: "Purchase Returns"
    });
  };

  const handleDeleteRow = (rowToDelete: any) => {
    try {
      const currentArray = [...returnProductsRef.current];
      const filtered = currentArray.filter(row => row.id !== rowToDelete.id);
      const updated = filtered.map((row, index) => ({
        ...row,
        id: index + 1
      }));
  
      const updatedEditedBatches = editedBatches.map((batchEntry: any) => {
        if (batchEntry.skuCode === rowToDelete.skuCode) {
          const filteredBatchIds = batchEntry.batchIds.filter((batchId: string) => {
            const batchStockType = batchId.split('_').pop();
            return batchStockType !== rowToDelete.stockType;
          });
          
          return {
            ...batchEntry,
            batchIds: filteredBatchIds
          };
        }
        return batchEntry;
      }).filter((batchEntry: any) => batchEntry.batchIds.length > 0);
  
      dispatch(setReturnProductsArray(updated));
      dispatch(setEditedBatches(updatedEditedBatches));
      
      const modified = modifyReturnProductsArray(updated, updatedEditedBatches);
      dispatch(setClaimSummaryRows(modified));
  
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  const rowsWithEligibility = returnProductsArray?.map((row:any) => ({
    ...row,
    isNonEligible: nonEligible?.includes(row.skuCode) || false
  })) || [];

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('purchaseReturns')) {
        dispatch(setReturnProductsArray([])); 
        dispatch(setEditedBatches([]));
        dispatch(setSelectedClaimSubType(""));
        dispatch(setNonEligible([]));
      }
    };
  }, []); 

  return (
    <>
      {rowsWithEligibility?.length ? (
        <div className="claims_table_container">
          <div className="flex-container">
            <MiniClaimSummary rows={returnProductsArray} />
            <button
              className="button-save-claim claim-custom"
              onClick={handleAddReturnProduct}
            >
              <span className="button-save-text-claim" style={{ color: "#1b7ea0" }}>
                +
              </span>
              <span className="button-save-text-claim" style={{ color: "#1b7ea0" }}>
                Add More
              </span>
            </button>
          </div>
          <CustomDataGrid
            columnsMetaData={purchaseReturnsColumnsMetaData}
            rows={rowsWithEligibility}
            insidePurchaseReturns={true}
            handleClickEditPurchaseReturnsRow={handleClickEditPurchaseReturnsRow}
            handleDeleteRow={handleDeleteRow}
            tableWidth={0.79}
          />
        </div>
      ) : (
        <EmptyPage
          height={87}
          text={"Add Purchased Products To Return!"}
          imgSrc={emptyPageImageSrc}
          button={
            <button
              className="button-save-claim"
              style={{ height: "34px", width: "125px", marginTop: "10px" }}
              onClick={handleAddReturnProduct}
            >
              <span className="button-save-text-claim">+ ADD</span>
            </button>
          }
        />
      )}
    </>
  );
};

export default PurchaseReturns;
